/**
 * Partner Portal (Stage 23 external side).
 * Standalone route — no AppShell, no Suntronix nav. Partners log in and see
 * ONLY their own Partner Work Orders. Commercial rates and internal SLA
 * targets are never sent to the browser.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Package, CheckCircle2, AlertTriangle, LogOut, Shield } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/partner-portal`;

const STATUS_STYLES = {
  assigned:    "bg-slate-100 text-slate-700 border-slate-200",
  accepted:    "bg-sky-100 text-sky-800 border-sky-200",
  scheduled:   "bg-indigo-100 text-indigo-800 border-indigo-200",
  in_progress: "bg-amber-100 text-amber-800 border-amber-200",
  completed:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:    "bg-rose-100 text-rose-800 border-rose-200",
  exception:   "bg-orange-100 text-orange-800 border-orange-200",
  cancelled:   "bg-slate-100 text-slate-500 border-slate-200",
  draft:       "bg-slate-100 text-slate-500 border-slate-200",
};

// Allowed next steps (must match backend PARTNER_ALLOWED_TRANSITIONS).
const NEXT = {
  assigned:    ["accepted", "rejected"],
  accepted:    ["scheduled", "in_progress", "exception"],
  scheduled:   ["in_progress", "exception"],
  in_progress: ["completed", "exception"],
};

const client = axios.create({ baseURL: API, withCredentials: true });

export default function PartnerPortal() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try { const { data } = await client.get("/auth/me"); setMe(data); }
    catch { setMe(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  if (loading) return <Splash>Loading Partner Portal…</Splash>;
  if (!me) return <PartnerLogin onLoggedIn={fetchMe} />;
  return <PartnerHome me={me} onLogout={() => { client.post("/auth/logout").finally(fetchMe); }} />;
}

function Splash({ children }) {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-sm text-slate-500 animate-pulse">{children}</div>
    </div>
  );
}

/* ============================================================ LOGIN */
function PartnerLogin({ onLoggedIn }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post("/auth/login", form);
      toast.success("Welcome to Partner Portal");
      onLoggedIn();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6" data-testid="partner-portal-login">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">SUNTRONIX ONE</div>
          <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">Partner Portal</h1>
          <p className="text-sm text-slate-600 mt-2">Scoped, secure access to the work orders assigned to you.</p>
        </div>
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md p-5 space-y-3">
          <label className="block">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Partner email</div>
            <input type="email" required className="input w-full" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="pp-email" autoFocus />
          </label>
          <label className="block">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Password</div>
            <input type="password" required className="input w-full" value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="pp-password" />
          </label>
          <button type="submit" disabled={busy}
            className="w-full h-10 rounded-md bg-slate-900 text-white text-sm font-medium disabled:opacity-60"
            data-testid="pp-login-submit">
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 pt-1 flex items-center gap-1">
            <Shield className="h-3 w-3" /> You will only see work orders assigned to your partner account.
          </div>
        </form>
      </div>
    </div>
  );
}

/* ============================================================ HOME */
function PartnerHome({ me, onLogout }) {
  const [tiles, setTiles] = useState(null);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("");
  const [active, setActive] = useState(null);

  const load = useCallback(() => {
    client.get("/summary").then(({ data }) => setTiles(data.tiles)).catch(() => {});
    client.get("/work-orders", { params: status ? { status } : {} })
      .then(({ data }) => { setItems(data.items || []); setCounts(data.counts || {}); })
      .catch(() => {});
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="partner-portal-home">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Partner Portal</div>
            <h1 className="text-xl font-heading font-semibold text-slate-900 tracking-tight" data-testid="pp-partner-name">
              {me.partner?.trading_name || me.partner?.legal_name}
            </h1>
            <div className="text-xs text-slate-500">{me.partner?.kind} · signed in as {me.partner_user?.email}</div>
          </div>
          <button onClick={onLogout} className="ml-auto h-9 px-3 text-sm rounded-md border border-slate-200 bg-white hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="pp-logout">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        {/* KPI tiles */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile icon={Package}       label="Open work orders"   value={tiles?.open_work_orders ?? "—"} testid="pp-tile-open" />
          <Tile icon={CheckCircle2}  label="Completed (30d)"    value={tiles?.completed_30d ?? "—"} tone="ok" testid="pp-tile-completed" />
          <Tile icon={Truck}         label="On-time % (30d)"    value={tiles?.on_time_pct_30d != null ? tiles.on_time_pct_30d + "%" : "—"} testid="pp-tile-ontime" />
          <Tile icon={AlertTriangle} label="Exceptions"         value={tiles?.exceptions_open ?? "—"} tone="warn" testid="pp-tile-exceptions" />
        </section>

        {/* Status filter chips */}
        <section className="flex flex-wrap gap-1 border-b border-slate-200">
          {["", "assigned", "accepted", "scheduled", "in_progress", "completed", "rejected", "exception"].map((s) => (
            <button key={s || "all"} onClick={() => setStatus(s)}
              data-testid={`pp-filter-${s || "all"}`}
              className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
                (status === s ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
              {s || "All"} {counts[s] != null && s !== "" && <span className="text-[10px] tabular-nums rounded bg-slate-200 text-slate-700 px-1">{counts[s]}</span>}
            </button>
          ))}
        </section>

        {/* Work orders list */}
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr>
                <th className="text-left py-2 px-3">PWO</th>
                <th className="text-left py-2 px-3">Service</th>
                <th className="text-left py-2 px-3">Where</th>
                <th className="text-left py-2 px-3">Required by</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500">No work orders in this view.</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="border-t border-slate-100" data-testid={`pp-pwo-${p.id}`}>
                  <td className="py-2 px-3 font-mono text-slate-900">{p.pwo_number}</td>
                  <td className="py-2 px-3 text-slate-800">{p.service}</td>
                  <td className="py-2 px-3 text-slate-600 text-xs">{[p.city, p.country].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="py-2 px-3 text-slate-600 text-xs">{p.required_by ? p.required_by.slice(0, 10) : "—"}</td>
                  <td className="py-2 px-3">
                    <span className={"inline-block text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + (STATUS_STYLES[p.status] || STATUS_STYLES.assigned)}>
                      {p.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={() => setActive(p)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50"
                      data-testid={`pp-open-${p.id}`}>Open</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* Notice */}
        <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1">
          <Shield className="h-3 w-3" /> Commercial rates and internal SLA targets are never shown in this portal.
        </div>
      </main>

      {active && <PwoDrawer pwo={active} onClose={() => setActive(null)} onChanged={() => { setActive(null); load(); }} />}
    </div>
  );
}

/* ============================================================ Tile */
function Tile({ icon: Icon, label, value, tone, testid }) {
  const color = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={"mt-1 text-2xl font-heading font-semibold tabular-nums " + color}>{value ?? "—"}</div>
    </div>
  );
}

/* ============================================================ Drawer / detail */
function PwoDrawer({ pwo, onClose, onChanged }) {
  const [next, setNext] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const options = NEXT[pwo.status] || [];

  const submit = async () => {
    if (!next) { toast.error("Choose the next status"); return; }
    if (next === "rejected" && !reason.trim()) { toast.error("Reject reason required"); return; }
    setBusy(true);
    try {
      await client.post(`/work-orders/${pwo.id}/status`, {
        status: next, reject_reason: reason || null, notes: notes || null,
      });
      toast.success(`Marked ${next}`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-40 flex justify-end" onClick={onClose} data-testid="pp-drawer">
      <div className="w-full max-w-lg h-full bg-white shadow-xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-slate-200">
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Work Order</div>
          <h2 className="text-xl font-heading font-semibold text-slate-900 tracking-tight mt-1">{pwo.pwo_number}</h2>
          <div className="text-xs text-slate-500 mt-1">Service · {pwo.service} · Current status <span className="uppercase tracking-widest">{pwo.status}</span></div>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <Row label="Where">{[pwo.address, pwo.city, pwo.country].filter(Boolean).join(", ") || "—"}</Row>
          <Row label="Recipient">{pwo.recipient_name || "—"}{pwo.contact_phone ? ` · ${pwo.contact_phone}` : ""}</Row>
          <Row label="Scheduled for">{pwo.scheduled_for || "—"}</Row>
          <Row label="Required by">{pwo.required_by || "—"}</Row>
          <Row label="Instructions">{pwo.instructions || "—"}</Row>
        </div>
        {options.length > 0 ? (
          <div className="p-5 border-t border-slate-200 space-y-3">
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Move to</div>
            <div className="flex flex-wrap gap-1">
              {options.map((o) => (
                <button key={o} onClick={() => setNext(o)}
                  data-testid={`pp-next-${o}`}
                  className={"px-3 py-1.5 rounded-md text-xs border " + (next === o ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-700")}>
                  {o}
                </button>
              ))}
            </div>
            {next === "rejected" && (
              <label className="block">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Reject reason</div>
                <input className="input w-full" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="pp-reason" />
              </label>
            )}
            <label className="block">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Notes (optional)</div>
              <textarea className="input w-full min-h-[70px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pp-notes" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Close</button>
              <button onClick={submit} disabled={busy || !next}
                className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white disabled:opacity-60"
                data-testid="pp-submit-status">
                {busy ? "Saving…" : "Confirm"}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 border-t border-slate-200 text-xs text-slate-500">
            No further transitions available from status {pwo.status}.
            <div className="mt-3 flex justify-end">
              <button onClick={onClose} className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 pt-0.5">{label}</div>
      <div className="col-span-2 text-slate-800">{children}</div>
    </div>
  );
}
